import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowUp, Bookmark, BookmarkMinus, ChevronDown } from "lucide-react";
import type { GroupeChoix } from "../nav/favoris";
import { Button } from "./Button";
import { Checkbox } from "./Checkbox";
import { useEnregistrerFavoris, useFavoris } from "../helpers/useFavoris";
import { basculer, deplacer, reinserer } from "../nav/favoris";
import dash from "./DashboardView.module.css";
import styles from "./MesFavoris.module.css";

/** Rubriques repliées à l'ouverture de l'éditeur, sauf si elles contiennent un favori (longues, rarement épinglées). */
const RUBRIQUES_REPLIEES = new Set(["Guides"]);

/** Rubriques ouvertes à l'ouverture de l'éditeur ; ensuite seul l'utilisateur les replie (rien ne se ferme sous le curseur). */
function rubriquesOuvertes(choix: GroupeChoix[], paths: string[]): Set<string> {
  return new Set(choix
    .filter((g) => g.pages.some((p) => paths.includes(p.path)) || !RUBRIQUES_REPLIEES.has(g.label))
    .map((g) => g.label));
}

/**
 * « Mes favoris » du tableau de bord (27/10/2026) : pages épinglées en signets, dans l'ordre
 * choisi, et leur éditeur. Mêmes favoris que le menu « Mon espace » (lien = `?favoris=1`).
 * Chaque changement est enregistré aussitôt et annoncé dans la barre d'état ; les contrôles ne sont
 * jamais désactivés pendant l'enregistrement, pour garder le focus clavier (double clic ignoré).
 */
export const MesFavoris: React.FC = () => {
  const { data, isLoading, isError: chargementRate, refetch } = useFavoris();
  const enregistrer = useEnregistrerFavoris();
  const [params, setParams] = useSearchParams();
  const [edition, setEdition] = useState(params.get("favoris") === "1");
  const [statut, setStatut] = useState("");
  const [deplace, setDeplace] = useState<string | null>(null);
  const [ouvertes, setOuvertes] = useState<Set<string>>(new Set());
  // Dernier retrait (annulable depuis la barre d'état) et dernière écriture (pour « Réessayer »).
  const [retrait, setRetrait] = useState<{ path: string; index: number; label: string } | null>(null);
  const derniere = useRef<{ paths: string[]; annonce: string } | null>(null);
  const arrivee = useRef(false);
  const section = useRef<HTMLElement>(null);
  const titreOrdre = useRef<HTMLHeadingElement>(null);
  const modifier = useRef<HTMLButtonElement>(null);
  const controles = useRef(new Map<string, HTMLButtonElement | null>());
  const aFocaliser = useRef<string | null>(null);

  // Arrivée depuis « Gérer mes favoris » du menu : paramètre retiré d'abord (le changement d'adresse
  // remet la page en haut), puis éditeur à l'écran et focus sur son titre.
  useEffect(() => {
    if (params.get("favoris") !== "1" || !data) return;
    arrivee.current = true;
    setOuvertes(rubriquesOuvertes(data.choix, data.favoris.map((f) => f.path)));
    setEdition(true);
    params.delete("favoris");
    setParams(params, { replace: true });
  }, [data, params, setParams]);
  useEffect(() => {
    if (!arrivee.current || !edition || params.has("favoris")) return;
    arrivee.current = false;
    const id = window.setTimeout(() => {
      section.current?.scrollIntoView({ block: "start" });
      titreOrdre.current?.focus({ preventScroll: true });
    }, 80);
    return () => window.clearTimeout(id);
  }, [edition, params]);

  // Après un déplacement ou un retrait, le focus revient sur le contrôle visé (la liste a été redessinée).
  useEffect(() => {
    const cle = aFocaliser.current;
    if (!cle) return;
    aFocaliser.current = null;
    (cle === "titre" ? titreOrdre.current : controles.current.get(cle))?.focus();
  }, [data]);

  if (chargementRate) {
    return (
      <section className={`${dash.sec} ${styles.section}`} aria-labelledby="h-fav">
        <div className={dash.secH}><h2 id="h-fav">Mes favoris</h2></div>
        <p className={styles.vide}>Vos favoris n’ont pas pu être chargés. <Button variant="outline" onClick={() => void refetch()}>Réessayer</Button></p>
      </section>
    );
  }
  if (isLoading || !data) return null;
  const paths = data.favoris.map((f) => f.path);
  const plein = paths.length >= data.max;
  const label = (path: string) => data.choix.flatMap((g) => g.pages).find((p) => p.path === path)?.label ?? path;
  const messagePlein = `${data.max} favoris au plus : retirez-en un pour en ajouter un autre.`;

  const ecrire = (suivants: string[] | null, annonce: string, focus?: string, ligne?: string, retire?: { path: string; index: number; label: string }) => {
    if (enregistrer.isPending) return;
    if (!suivants) {
      setStatut(messagePlein);
      return;
    }
    derniere.current = { paths: suivants, annonce };
    enregistrer.mutate(suivants, {
      onSuccess: () => {
        setStatut(annonce);
        setRetrait(retire ?? null);
        setDeplace(ligne ?? null);
        aFocaliser.current = focus ?? null;
      },
    });
  };
  const annuler = () => {
    if (!retrait) return;
    ecrire(reinserer(paths, retrait.path, retrait.index), `Remis en position ${retrait.index + 1} : ${retrait.label}.`, `retrait:${retrait.path}`, retrait.path);
  };
  const reessayer = () => { if (derniere.current) ecrire(derniere.current.paths, derniere.current.annonce); };
  const ouvrir = () => {
    setOuvertes(rubriquesOuvertes(data.choix, paths));
    setEdition(true);
    setStatut("");
    setRetrait(null);
    requestAnimationFrame(() => {
      section.current?.scrollIntoView({ block: "start", behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
      titreOrdre.current?.focus({ preventScroll: true });
    });
  };
  const fermer = () => {
    setEdition(false);
    setStatut("");
    setRetrait(null);
    requestAnimationFrame(() => modifier.current?.focus());
  };
  const reference = (cle: string) => (el: HTMLButtonElement | null) => { controles.current.set(cle, el); };

  return (
    <section ref={section} className={`${dash.sec} ${styles.section}`} aria-labelledby="h-fav">
      <div className={dash.secH}>
        <h2 id="h-fav">Mes favoris</h2>
        {!edition && paths.length > 0 && (
          <button ref={modifier} type="button" className={`${dash.tlink} ${styles.lien}`} onClick={ouvrir}>
            Modifier
          </button>
        )}
      </div>

      {!edition && paths.length === 0 && (
        <div className={styles.vide}>
          <p>Épinglez les pages que vous ouvrez le plus souvent : elles s’afficheront ici et dans le menu Mon espace.</p>
          <Button ref={modifier} variant="outline" onClick={ouvrir}><Bookmark aria-hidden="true" />Choisir mes favoris</Button>
        </div>
      )}

      {!edition && paths.length > 0 && (
        <ol className={styles.rangee} aria-label="Mes favoris, dans l’ordre choisi">
          {data.favoris.map((f) => {
            const contenu = <><Bookmark className={styles.signet} aria-hidden="true" /><span>{f.label}</span></>;
            return (
              <li key={f.path}>
                {f.isStatic ? <a href={f.href}>{contenu}</a> : <Link to={f.href}>{contenu}</Link>}
              </li>
            );
          })}
        </ol>
      )}

      {edition && (
        <div className={styles.edition} id="km-fav-edition">
          <div className={styles.ordre}>
            <h3 ref={titreOrdre} tabIndex={-1}>Ordre d’affichage</h3>
            {paths.length === 0 ? <p className={dash.d}>Aucun favori pour l’instant : cochez des pages dans la liste.</p> : (
              <ol>
                {data.favoris.map((f, i) => {
                  const premier = i === 0;
                  const dernier = i === paths.length - 1;
                  const suivant = data.favoris[i + 1]?.path ?? data.favoris[i - 1]?.path;
                  return (
                    <li key={f.path} className={deplace === f.path ? styles.bouge : undefined}>
                      <span className={styles.rang} aria-hidden="true">{i + 1}.</span>
                      <span className={styles.nom}>{f.label}</span>
                      <span className={styles.fleches}>
                        <Button ref={reference(`haut:${f.path}`)} variant="ghost" size="icon" aria-label={`Monter ${f.label}`} aria-disabled={premier || undefined}
                          onClick={() => !premier && ecrire(deplacer(paths, i, -1), `Déplacé en position ${i} : ${f.label}.`, `haut:${f.path}`, f.path)}>
                          <ArrowUp aria-hidden="true" />
                        </Button>
                        <Button ref={reference(`bas:${f.path}`)} variant="ghost" size="icon" aria-label={`Descendre ${f.label}`} aria-disabled={dernier || undefined}
                          onClick={() => !dernier && ecrire(deplacer(paths, i, 1), `Déplacé en position ${i + 2} : ${f.label}.`, `bas:${f.path}`, f.path)}>
                          <ArrowDown aria-hidden="true" />
                        </Button>
                      </span>
                      <Button ref={reference(`retrait:${f.path}`)} variant="ghost" size="icon" className={styles.retirer} aria-label={`Retirer ${f.label} des favoris`}
                        onClick={() => ecrire(basculer(paths, f.path, data.max), `Retiré des favoris : ${f.label}.`, suivant ? `retrait:${suivant}` : "titre", undefined, { path: f.path, index: i, label: f.label })}>
                        <BookmarkMinus aria-hidden="true" />
                      </Button>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          <div className={styles.choix}>
            {plein && <p className={styles.plein} id="km-fav-plein">{messagePlein}</p>}
            {data.choix.map((g) => {
              const coches = g.pages.filter((p) => paths.includes(p.path)).length;
              return (
                <details key={g.label} open={ouvertes.has(g.label)}
                  onToggle={(e) => {
                    const ouverte = e.currentTarget.open;
                    setOuvertes((avant) => {
                      if (avant.has(g.label) === ouverte) return avant;
                      const apres = new Set(avant);
                      if (ouverte) apres.add(g.label); else apres.delete(g.label);
                      return apres;
                    });
                  }}>
                  <summary>
                    <ChevronDown className={styles.chevron} aria-hidden="true" />
                    {g.label}
                    <small>{coches > 0 ? `${coches} épinglée${coches > 1 ? "s" : ""}` : `${g.pages.length} pages`}</small>
                  </summary>
                  <div className={styles.pages}>
                    {g.pages.map((p) => {
                      const coche = paths.includes(p.path);
                      const bloque = !coche && plein;
                      return (
                        <label key={p.path} className={styles.page} data-plein={bloque ? "" : undefined}>
                          <Checkbox
                            checked={coche}
                            aria-disabled={bloque || undefined}
                            aria-describedby={bloque ? "km-fav-plein" : undefined}
                            onChange={() => ecrire(basculer(paths, p.path, data.max), coche ? `Retiré des favoris : ${label(p.path)}.` : `Ajouté aux favoris : ${label(p.path)}.`)}
                          />
                          {p.label}
                        </label>
                      );
                    })}
                  </div>
                </details>
              );
            })}
          </div>

          {/* Barre collée en bas : le retour d'enregistrement reste sous les yeux, même loin dans la liste. */}
          <div className={styles.barre}>
            <p className={enregistrer.isError ? styles.erreur : styles.statut} role="status" aria-live="polite">
              {enregistrer.isError ? "Favoris non enregistrés : vérifiez votre connexion." : statut || `${paths.length} favori${paths.length > 1 ? "s" : ""} sur ${data.max}`}
            </p>
            {enregistrer.isError
              ? <Button variant="outline" onClick={reessayer}>Réessayer</Button>
              : retrait && <Button variant="outline" onClick={annuler}>Annuler</Button>}
            <Button variant="primary" onClick={fermer}>Terminer</Button>
          </div>
        </div>
      )}
    </section>
  );
};
